import * as React from 'react';
import { memo, useMemo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import { ErrorsType, ICountry, TeacherHireType, TeacherSEType } from 'types';

import { InputLabel, ISelectOption, Select, TextArea, Input } from 'atoms';

export interface ISeFieldsValues extends Partial<TeacherSEType> {}

export interface ISeFields {
  values?: ISeFieldsValues;
  disableFields?: boolean;
  countries: ISelectOption<string>[];
  index: number;
  errors?: ErrorsType<ISeFieldsValues> | null;
  onInputChange: (
    name: string,
    value?: string | boolean | ISelectOption<string> | null | ICountry,
  ) => void;
}

export const SeFields = memo(
  ({
    values,
    disableFields,
    index,
    countries,
    errors,
    onInputChange,
  }: ISeFields) => {
    const { t } = useTranslation();
    const name = `teacherTypes[${index}].${TeacherHireType.Se}`;

    const nationalityValue = useMemo(
      () =>
        values?.nationality
          ? {
              value: values.nationality.id,
              label: values.nationality.title,
            }
          : null,
      [values?.nationality],
    );

    return (
      <>
        <div>
          <InputLabel>
            {t('teacherTypePopup.seFields.passportNumber.label')}
          </InputLabel>
          <Input
            placeholder={t(
              'teacherTypePopup.seFields.passportNumber.placeholder',
            )}
            max={100}
            type="text"
            value={values?.passportNumber || ''}
            name={`${name}.passportNumber`}
            onChange={({ target: { value } }) =>
              onInputChange(`${name}.passportNumber`, value)
            }
            hasError={Boolean(errors?.passportNumber)}
            disabled={disableFields}
          />
        </div>
        <div>
          <InputLabel>{t('teacherTypePopup.seFields.inn.label')}</InputLabel>
          <Input
            placeholder={t('teacherTypePopup.seFields.inn.placeholder')}
            max={100}
            type="text"
            value={values?.inn || ''}
            name={`${name}.inn`}
            onChange={({ target: { value } }) =>
              onInputChange(`${name}.inn`, value)
            }
            hasError={Boolean(errors?.inn)}
            disabled={disableFields}
          />
        </div>
        <div>
          <InputLabel>
            {t('teacherTypePopup.seFields.nationality.label')}
          </InputLabel>
          <Select
            value={nationalityValue}
            type="default"
            placeholder={t('teacherTypePopup.seFields.nationality.placeholder')}
            options={countries}
            onChange={(selectValue) =>
              onInputChange(`${name}.nationality`, {
                id: selectValue?.value,
                title: selectValue?.label,
              })
            }
            hasError={Boolean(errors?.nationality)}
            isDisabled={disableFields}
          />
        </div>
        <div>
          <InputLabel>
            {t('teacherTypePopup.seFields.accountNumber.label')}
          </InputLabel>
          <Input
            placeholder={t(
              'teacherTypePopup.seFields.accountNumber.placeholder',
            )}
            max={100}
            type="text"
            value={values?.accountNumber || ''}
            name={`${name}.accountNumber`}
            onChange={({ target: { value } }) =>
              onInputChange(`${name}.accountNumber`, value)
            }
            hasError={Boolean(errors?.accountNumber)}
            disabled={disableFields}
          />
        </div>
        <div>
          <InputLabel>{t('teacherTypePopup.seFields.bic.label')}</InputLabel>
          <Input
            placeholder={t('teacherTypePopup.seFields.bic.placeholder')}
            max={100}
            type="text"
            value={values?.bic || ''}
            name={`${name}.bic`}
            onChange={({ target: { value } }) =>
              onInputChange(`${name}.bic`, value)
            }
            hasError={Boolean(errors?.bic)}
            disabled={disableFields}
          />
        </div>
        <div>
          <InputLabel>
            {t('teacherTypePopup.seFields.bankTitle.label')}
          </InputLabel>
          <Input
            placeholder={t('teacherTypePopup.seFields.bankTitle.placeholder')}
            max={100}
            type="text"
            value={values?.bankTitle || ''}
            name={`${name}.bankTitle`}
            onChange={({ target: { value } }) =>
              onInputChange(`${name}.bankTitle`, value)
            }
            hasError={Boolean(errors?.bankTitle)}
            disabled={disableFields}
          />
        </div>
        <TextAreaWrapper>
          <InputLabel>
            {t('teacherTypePopup.seFields.registrationAddress.label')}
          </InputLabel>
          <TextArea
            placeholder={t(
              'teacherTypePopup.seFields.registrationAddress.placeholder',
            )}
            rows={1}
            value={values?.registrationAddress || ''}
            name={`${name}.registrationAddress`}
            onChange={({ target: { value } }) =>
              onInputChange(`${name}.registrationAddress`, value)
            }
            hasError={Boolean(errors?.registrationAddress)}
            disabled={disableFields}
          />
        </TextAreaWrapper>
      </>
    );
  },
);

const TextAreaWrapper = styled.div`
  grid-column-start: 1;
  grid-column-end: 3;
`;
